import { Popover } from '@headlessui/react';
import ToastStack from 'components/desktop/shared/toast-stack';
import { IconClose, IconPaperPlane, IconSmile } from 'components/icons';
import { useAuthenticationContext } from 'components/providers/authentication-provider';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { CommentForm, commentFormDefaultValues } from 'forms/temp/comment';
import { SITE_MAP } from 'globalConstants';
import { Post } from 'graphql/main/mutations/temp/create-post';
import {
  CREATE_COMMENT,
  CreateCommentData,
  CreateCommentVariables,
} from 'graphql/main/mutations/temp/createComment';
import { FileWithFullUrls } from 'graphql/main/queries';
import { Comment } from 'graphql/main/queries/temp/comments';
import { useFlexMutation, useToast } from 'hooks';
import { isEmpty, isEqual, map, size, trim } from 'lodash';
import Link from 'next/link';
import { Fragment, KeyboardEvent, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import CommentNode from './comment-node';

interface Props {
  comment?: Comment;
  post?: Post;
  onCompleteCommenting?: () => void;
}

const CommentTree = ({ comment, post, onCompleteCommenting }: Props) => {
  const { toasts, addToast, removeToast } = useToast();
  const { currentUser } = useAuthenticationContext();
  const { id: commentId } = comment ?? {};
  const { id: postId } = post ?? {};
  const [showingEmoji, setShowingEmoji] = useState<boolean>(false);
  const [showingChildren, setShowingChildren] = useState(false);
  const [showingReplyComment, setShowingReplyComment] = useState(false);
  const { control, getValues, setValue } = useForm<CommentForm>({
    defaultValues: commentFormDefaultValues,
  });
  const bodyWatch = useWatch({
    control,
    name: 'body',
  });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [createComment] = useFlexMutation<CreateCommentData, CreateCommentVariables>(
    'main',
    CREATE_COMMENT,
  );

  const reply = () => {
    setShowingReplyComment(true);
    inputRef.current?.focus();
  };
  const chooseEmoji = (_: EmojiClickData, event: any) => {
    setValue('body', getValues('body') + event.emoji);
    inputRef.current?.focus();
  };
  const keyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (isEqual(event.key, 'Enter')) {
      const { body } = getValues();
      if (postId && commentId && body && !isEmpty(trim(body))) {
        const { data: createCommentData, errors: createCommentErrors } = await createComment({
          variables: {
            body: trim(body),
            parentCommentId: commentId,
            postId,
          },
        });
        if (createCommentErrors) {
          addToast('error', createCommentErrors?.[0]?.message);
        } else {
          setShowingChildren(true);
          if (comment?.replyComments && createCommentData?.createComment?.data) {
            comment.replyComments = [
              createCommentData.createComment.data,
              ...comment.replyComments,
            ];
          }
          await onCompleteCommenting?.();
        }
      }
      setValue('body', '');
    }
  };

  return (
    <Fragment>
      <CommentNode comment={comment} onReply={reply} />
      <Fragment>
        <button
          type='button'
          className={`pl-[48px] overflow-hidden ${
            !isEmpty(comment?.replyComments) ? 'h-auto' : 'h-0 !mt-0'
          }`}
          onClick={() => setShowingChildren(!showingChildren)}
        >
          <span className='font-[600] text-primary text-[12px] leading-[15px] hover:underline'>
            {showingChildren ? 'Ẩn' : 'Xem'} {size(comment?.replyComments)} phản hồi
          </span>
        </button>
        <div
          className={`space-y-[12px] overflow-hidden pl-[48px] w-full !mt-0 ${
            showingChildren ? 'max-h-full !mt-[12px]' : 'max-h-0'
          }`}
        >
          {map(comment?.replyComments, (replyComment, replyCommentIndex) => (
            <CommentNode
              key={`${replyComment?.id}-${replyCommentIndex}`}
              comment={replyComment}
              onReply={reply}
            />
          ))}
        </div>
      </Fragment>
      <div
        className={`flex w-full items-center pl-[48px] overflow-hidden ${
          showingReplyComment ? 'h-auto' : 'h-0 !mt-0'
        }`}
      >
        <div className='flex items-center space-x-[10px] w-full'>
          <Link
            href={`/${SITE_MAP.SOCIAL_NETWORK.INDEX}/${SITE_MAP.SOCIAL_NETWORK.SOCIAL_PROFILE}/${currentUser?.id}`}
          >
            <div className='h-[36px] w-[36px] cursor-pointer overflow-hidden rounded-full'>
              <img
                src={
                  (currentUser?.avatar as FileWithFullUrls)?.originalUrl ??
                  '/images/default-avatar.svg'
                }
                alt={currentUser?.fullName}
                loading='lazy'
                className='h-full w-full object-cover'
              />
            </div>
          </Link>
          <div className='group w-full flex h-[40px] cursor-text items-center rounded-[12px] border border-stroke'>
            <input
              ref={inputRef}
              type='text'
              value={bodyWatch}
              placeholder='Nhập bình luận của bạn'
              className='w-full bg-transparent px-[12px] py-[10px] placeholder-text2'
              onChange={(event) => setValue('body', event.target.value)}
              onKeyDown={keyDown}
            />
            {Boolean(bodyWatch) && (
              <button
                type='button'
                className='mr-[12px] hidden group-hover:flex'
                onClick={() => setValue('body', '')}
              >
                <IconClose className='h-[20px] w-[20px] text-text2' />
              </button>
            )}
            <Popover className='relative'>
              <Popover.Button
                type='button'
                className='flex items-center mr-[12px]'
                onClick={() => setShowingEmoji?.(!showingEmoji)}
              >
                <IconSmile className='h-[18px] w-[18px] text-text2' />
              </Popover.Button>
              <Popover.Panel className='absolute bottom-[40px] right-0 cursor-pointer'>
                <EmojiPicker
                  searchDisabled
                  previewConfig={{
                    showPreview: false,
                  }}
                  onEmojiClick={chooseEmoji}
                />
              </Popover.Panel>
            </Popover>
          </div>
          <button
            type='button'
            disabled={isEmpty(trim(bodyWatch))}
            className='flex h-[36px] w-[36px] items-center justify-center rounded-full bg-secondary transition duration-[200ms] ease-in-out hover:bg-secondary-light disabled:opacity-50 disabled:hover:bg-secondary'
          >
            <IconPaperPlane className='h-[22px] w-[22px] text-primary' />
          </button>
        </div>
      </div>
      <ToastStack toasts={toasts} removeToast={removeToast} />
    </Fragment>
  );
};

export default CommentTree;
